/**
 * EEMENTOR product tabs: accessible tabs and carousel arrows.
 * Add to cart is handled by ee-product-card.js.
 */
class EeProductTabs extends HTMLElement {
  connectedCallback() {
    this.tabs = [...this.querySelectorAll('[role="tab"]')];

    this.tabs.forEach((tab) => {
      tab.addEventListener('click', () => this.select(tab));
      tab.addEventListener('keydown', this.onTabKeydown);
    });

    this.querySelectorAll('.ee-carousel').forEach((carousel) => this.initCarousel(carousel));
  }

  /** @param {Element} tab */
  select(tab, focus = false) {
    this.tabs.forEach((t) => {
      const selected = t === tab;
      t.setAttribute('aria-selected', String(selected));
      t.tabIndex = selected ? 0 : -1;
      const panel = this.querySelector(`#${t.getAttribute('aria-controls')}`);
      if (panel) panel.hidden = !selected;
    });
    if (focus) tab.focus();

    const panel = this.querySelector(`#${tab.getAttribute('aria-controls')}`);
    const carousel = panel?.querySelector('.ee-carousel');
    if (carousel) this.updateCarousel(carousel);
  }

  /** @param {KeyboardEvent} event */
  onTabKeydown = (event) => {
    const index = this.tabs.indexOf(event.currentTarget);
    const last = this.tabs.length - 1;
    const map = { ArrowRight: index + 1, ArrowLeft: index - 1, Home: 0, End: last };
    if (!(event.key in map)) return;
    event.preventDefault();
    let next = map[event.key];
    if (next > last) next = 0;
    if (next < 0) next = last;
    this.select(this.tabs[next], true);
  };

  /** @param {Element} carousel */
  initCarousel(carousel) {
    const track = carousel.querySelector('.ee-carousel__track');
    carousel.querySelectorAll('.ee-carousel__arrow').forEach((arrow) => {
      arrow.addEventListener('click', () => {
        const card = track.querySelector('.ee-card');
        const gap = parseFloat(getComputedStyle(track).columnGap) || 0;
        const step = card ? card.getBoundingClientRect().width + gap : track.clientWidth;
        const perView = Math.max(1, Math.floor((track.clientWidth + gap) / step));
        track.scrollBy({ left: Number(arrow.dataset.dir) * step * perView });
      });
    });
    track.addEventListener('scroll', () => this.updateCarousel(carousel), { passive: true });
    new ResizeObserver(() => this.updateCarousel(carousel)).observe(track);
    this.updateCarousel(carousel);
  }

  /** @param {Element} carousel */
  updateCarousel(carousel) {
    const track = carousel.querySelector('.ee-carousel__track');
    const max = track.scrollWidth - track.clientWidth;
    carousel.dataset.scrollable = String(max > 2);
    const [prev, next] = carousel.querySelectorAll('.ee-carousel__arrow');
    if (prev) prev.disabled = track.scrollLeft <= 2;
    if (next) next.disabled = track.scrollLeft >= max - 2;
  }
}

if (!customElements.get('ee-product-tabs')) customElements.define('ee-product-tabs', EeProductTabs);
